import { ReactElement, ReactNode, useState, useEffect, useRef } from 'react';
import Spinner from './Spinner';
import { HiOutlinePencilSquare } from 'react-icons/hi2';

interface DetailCardProps {
  label: string;
  children: ReactNode | ((isEdit: boolean, setIsSaving: (saving: boolean) => void) => ReactNode);
  canEdit?: boolean;
  onSave?: () => void;
  excludeFromClickOutside?: string[]; // Array of CSS selectors to exclude
  className?: string; // Additional CSS classes
}

export default function DetailCard({
  label,
  children,
  canEdit = false,
  onSave,
  excludeFromClickOutside = [],
  className = '',
}: DetailCardProps): ReactElement {
  const [isEdit, setIsEdit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleCardClick = () => {
    if (canEdit && !isEdit) {
      setIsEdit(true);
    }
  };

  const handleExitEdit = () => {
    if (onSave) {
      onSave();
    }
    setIsEdit(false);
  };

  // Check if element is part of an overlay/modal
  const isOverlayElement = (element: Element): boolean => {
    // Check if element is inside any of the excluded selectors
    for (const selector of excludeFromClickOutside) {
      if (element.closest(selector)) {
        return true;
      }
    }

    // Common overlay/modal selectors
    const overlaySelectors = [
      '[role="dialog"]',
      '[role="menu"]',
      '[role="listbox"]',
      '[role="combobox"]',
      '.MuiPopover-root',
      '.MuiModal-root',
      '.MuiPopper-root',
      '.react-datepicker',
      '.calendar-overlay',
      '.datepicker-overlay',
    ];

    for (const selector of overlaySelectors) {
      if (element.closest(selector)) {
        return true;
      }
    }

    return false;
  };

  // Handle click outside to exit edit mode
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isEdit || !cardRef.current) return;

      const target = event.target as Element;

      // Don't exit if click is inside the card
      if (cardRef.current.contains(target)) {
        return;
      }

      // Don't exit if click is on an overlay element
      if (isOverlayElement(target)) {
        return;
      }

      handleExitEdit();
    };

    // Handle keyboard events
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEdit && event.key === 'Escape') {
        handleExitEdit();
      }
    };

    if (isEdit) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isEdit, excludeFromClickOutside]);

  return (
    // Below lg the card is a compact label-left / value-right row: the stacked,
    // centred desktop treatment burned ~130px of phone height per card for a
    // single line of text, and three of them overflowed a tablet content
    // column. From lg up the layout is unchanged.
    <div
      ref={cardRef}
      className={`cr-group cr-relative cr-flex cr-w-full cr-flex-row cr-items-center cr-gap-3 cr-rounded-lg
                  cr-bg-white cr-px-3 cr-py-2 cr-text-left cr-shadow-md
                  lg:cr-w-56 lg:cr-flex-col lg:cr-items-stretch lg:cr-gap-0 lg:cr-p-2 lg:cr-text-center
                  ${canEdit && !isEdit ? 'cr-cursor-pointer' : ''} ${className}`}
      onClick={handleCardClick}
    >
      {canEdit && !isEdit && !isSaving && (
        <div className="cr-absolute cr-right-2 cr-top-2 cr-hidden cr-opacity-0 cr-transition-opacity cr-duration-200 group-hover:cr-opacity-100 lg:cr-block">
          <HiOutlinePencilSquare className="cr-text-xl cr-text-blue-900" />
        </div>
      )}
      <div className="cr-absolute cr-right-2 cr-top-2">
        <Spinner isFetching={isSaving} />
      </div>
      <label className={
        'font-montserrat cr-shrink-0 cr-basis-24 cr-text-xs cr-font-bold cr-uppercase cr-text-blue-900 '
        + 'lg:cr-m-4 lg:cr-basis-auto lg:cr-text-lg'
      }>
        {label}
      </label>
      <div className={
        'cr-min-w-0 cr-flex-1 cr-overflow-hidden cr-font-main cr-text-sm cr-text-blue-900 '
        + 'lg:cr-m-2 lg:cr-w-full lg:cr-flex-none lg:cr-text-base'
      }>
        {typeof children === 'function' ? children(isEdit, setIsSaving) : children}
      </div>
    </div>
  );
}
